import { buildSeconds } from './config.js';

/** Metres per degree of latitude (close enough for field-sized geometry). */
const M_PER_DEG_LAT = 111_320;

/**
 * A circle of `meters` radius as a GeoJSON polygon.
 *
 * Energy fields are real distances on the ground, so they must be drawn as
 * geometry — a fixed pixel radius would shrink and grow as you zoom, which is
 * exactly what makes a 30m field look like a 3km one.
 */
export function circlePolygon(lng, lat, meters, steps = 64) {
  const coords = [];
  const dLat = meters / M_PER_DEG_LAT;
  const dLng = meters / (M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= steps; i++) {
    const theta = (i / steps) * 2 * Math.PI;
    coords.push([lng + dLng * Math.cos(theta), lat + dLat * Math.sin(theta)]);
  }
  return { type: 'Polygon', coordinates: [coords] };
}

/** Great-circle distance in metres. */
export function distanceMeters(a, b) {
  const R = 6_371_000;
  const φ1 = (a.lat * Math.PI) / 180;
  const φ2 = (b.lat * Math.PI) / 180;
  const dφ = φ2 - φ1;
  const dλ = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** A point `meters` away from (lng, lat) at `angle` radians (0 = east). */
export function offsetPoint(lng, lat, meters, angle) {
  const dLat = meters / M_PER_DEG_LAT;
  const dLng = meters / (M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));
  return [lng + dLng * Math.cos(angle), lat + dLat * Math.sin(angle)];
}

/** An arc of `meters` radius from `fromDeg` to `toDeg` (compass degrees, clockwise from north). */
export function arcLine(lng, lat, meters, fromDeg, toDeg, steps = 48) {
  const coords = [];
  const n = Math.max(2, Math.round((steps * Math.abs(toDeg - fromDeg)) / 360));
  for (let i = 0; i <= n; i++) {
    const deg = fromDeg + ((toDeg - fromDeg) * i) / n;
    const theta = ((90 - deg) * Math.PI) / 180;
    coords.push(offsetPoint(lng, lat, meters, theta));
  }
  return { type: 'LineString', coordinates: coords };
}

/**
 * How far through its build a field is, 0..1. The server does not return the
 * creation time, but the build length is a pure function of the radius that
 * the client mirrors (buildSeconds), so the start is activates_at minus that.
 */
export function buildProgress(field, now = Date.now()) {
  const end = new Date(field.activates_at).getTime();
  const start = field.created_at
    ? new Date(field.created_at).getTime()
    : end - buildSeconds(field.radius_m ?? field.radiusMeters ?? 30) * 1000;
  if (!(end > start)) return 1;
  return Math.min(1, Math.max(0, (now - start) / (end - start)));
}
